import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import FactoryAttendance from './components/FactoryAttendance';
import SupervisorDashboard from './components/SupervisorDashboard';
import OwnerConfiguration from './components/OwnerConfiguration';
import OwnerEmployees from './components/OwnerEmployees';
import OwnerSupervision from './components/OwnerSupervision';
import DirectMessages from './components/DirectMessages';
import EmployeeProfile from './components/EmployeeProfile';
import { attendanceAPI, managementAPI } from './services/api';

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const DashboardRouter = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(user?.role === 'owner' ? 'configuracion' : 'fichada');
  const [showProfile, setShowProfile] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(localStorage.getItem('selected_company_id') || '');
  const [companyLoading, setCompanyLoading] = useState(false);
  const [employeeFactories, setEmployeeFactories] = useState([]);
  /** Al abrir Mensajes desde supervisión: `userId:factoryId` para seleccionar esa conversación */
  const [dmPendingConversationKey, setDmPendingConversationKey] = useState(null);

  const handleLogout = () => {
    logout();
  };

  const loadCompanies = async () => {
    if (user?.role !== 'owner') return;
    const res = await managementAPI.companies();
    const rows = res.data || [];
    setCompanies(rows);
    const current = selectedCompanyId;
    const exists = rows.some((c) => String(c.id) === String(current));
    const next = exists ? String(current) : (rows[0] ? String(rows[0].id) : '');
    setSelectedCompanyId(next);
    localStorage.setItem('selected_company_id', next);
  };

  useEffect(() => {
    (async () => {
      if (user?.role !== 'owner') return;
      try {
        setCompanyLoading(true);
        await loadCompanies();
      } finally {
        setCompanyLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  useEffect(() => {
    (async () => {
      if (user?.role !== 'employee') {
        setEmployeeFactories([]);
        return;
      }
      try {
        const res = await attendanceAPI.myFactories();
        setEmployeeFactories(res.data || []);
      } catch {
        setEmployeeFactories([]);
      }
    })();
  }, [user?.id, user?.role]);

  const showSupervisorDashboard = user?.role === 'employee' && employeeFactories.some((f) => f.is_supervisor);

  useEffect(() => {
    if (activeTab === 'supervisor-dashboard' && !showSupervisorDashboard) {
      setActiveTab('fichada');
    }
  }, [activeTab, showSupervisorDashboard]);

  const handleQuickCreateCompany = async () => {
    const name = window.prompt('Nombre de la empresa');
    if (!name || !name.trim()) return;
    try {
      setCompanyLoading(true);
      const res = await managementAPI.createCompany({ name: name.trim() });
      const newId = String(res.data?.id || '');
      await loadCompanies();
      if (newId) {
        setSelectedCompanyId(newId);
        localStorage.setItem('selected_company_id', newId);
      }
      setShowProfile(false);
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'No se pudo crear la empresa');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Mobile navigation items for different roles
  const getNavigationItems = () => {
    if (user?.role === 'owner') {
      return [
        { id: 'configuracion', label: 'Configuración', icon: '⚙️' },
        { id: 'employees', label: 'Empleados', icon: '👥' },
        { id: 'supervision', label: 'Supervisión', icon: '🔗' },
        { id: 'dm', label: 'Mensajes', icon: '💬' },
      ];
    }
    const items = [{ id: 'fichada', label: 'Fichada', icon: '🏭' }];
    if (showSupervisorDashboard) {
      items.push({ id: 'supervisor-dashboard', label: 'Dashboard', icon: '📊' });
    }
    items.push({ id: 'dm', label: 'Mensajes', icon: '💬' }, { id: 'profile', label: 'Perfil', icon: '👤' });
    return items;
  };

  const navigationItems = getNavigationItems();
  const isOwner = user?.role === 'owner';
  const useEmployeeDesktopShell = user?.role === 'employee' && showSupervisorDashboard;

  const employeeDesktopHeader = useMemo(() => {
    switch (activeTab) {
      case 'fichada':
        return { title: 'Fichada', subtitle: 'Marcá entrada y salida en tu sede asignada.' };
      case 'supervisor-dashboard':
        return { title: 'Supervisión', subtitle: 'Vista del día del equipo: turnos, fichadas y estado.' };
      case 'dm':
        return { title: 'Mensajes', subtitle: 'Conversaciones con tus contactos por sede.' };
      case 'profile':
        return { title: 'Perfil', subtitle: 'Tus datos, sedes y rol.' };
      default:
        return { title: 'SiCheck', subtitle: '' };
    }
  }, [activeTab]);

  const isDmView = user?.role === 'employee' && activeTab === 'dm';

  const renderMainContent = () => {
    if (user?.role === 'owner' && activeTab === 'configuracion') {
      return <OwnerConfiguration selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />;
    }
    if (user?.role === 'owner' && activeTab === 'employees') {
      return <OwnerEmployees selectedCompanyId={selectedCompanyId} />;
    }
    if (user?.role === 'owner' && activeTab === 'supervision') {
      return <OwnerSupervision selectedCompanyId={selectedCompanyId} />;
    }
    if (activeTab === 'dm') {
      return (
        <DirectMessages
          pendingConversationKey={dmPendingConversationKey}
          onPendingConversationConsumed={() => setDmPendingConversationKey(null)}
          selectedCompanyId={selectedCompanyId}
        />
      );
    }
    if (user?.role === 'employee' && activeTab === 'profile') {
      return <EmployeeProfile />;
    }
    if (user?.role === 'employee' && activeTab === 'supervisor-dashboard' && showSupervisorDashboard) {
      return (
        <SupervisorDashboard
          onOpenMessagesWithEmployee={(employeeUserId, factoryId) => {
            setDmPendingConversationKey(`${Number(employeeUserId)}:${Number(factoryId)}`);
            setActiveTab('dm');
          }}
        />
      );
    }
    if (activeTab === 'fichada') {
      return <FactoryAttendance selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />;
    }
    return user?.role === 'owner'
      ? <OwnerConfiguration selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />
      : <FactoryAttendance selectedCompanyId={selectedCompanyId} onCompanyCreated={loadCompanies} />;
  };

  return (
    <div
      className={`app-container${isDmView ? ' app-container--dm' : ''}${isOwner ? ' app-container--owner' : ''}${
        useEmployeeDesktopShell ? ' app-container--employee-desktop' : ''
      }`}
    >
      {isOwner ? (
        <>
          {/* Owner mobile shell */}
          <div className="owner-mobile-shell">
            <header className="mobile-header">
              <div className="flex items-center gap-2">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-8 w-8 flex-shrink-0 rounded-lg"
                  width={32}
                  height={32}
                />
                <h1>SiCheck</h1>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center touch-feedback"
                >
                  👤
                </button>
              </div>
            </header>
            <main className="content-with-nav container-mobile">
              {renderMainContent()}
            </main>
            <nav className="bottom-nav">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`bottom-nav-item ${activeTab === item.id ? 'active' : ''}`}
                >
                  <span className="text-xl mb-1">{item.icon}</span>
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Owner desktop shell */}
          <div className="owner-desktop-shell">
            <aside className="owner-desktop-sidebar">
              <div className="owner-desktop-brand">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-9 w-9 rounded-xl"
                  width={36}
                  height={36}
                />
                <div>
                  <p className="owner-desktop-brand-title">SiCheck</p>
                </div>
              </div>
              <div className="owner-desktop-nav">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`owner-desktop-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  >
                    <span className="text-base">{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </aside>
            <div className="owner-desktop-main">
              <header className="owner-desktop-header">
                <div>
                  <h2 className="owner-desktop-title">
                    {activeTab === 'employees'
                      ? 'Empleados'
                      : activeTab === 'supervision'
                        ? 'Supervisión'
                        : activeTab === 'dm'
                          ? 'Mensajes'
                          : 'Configuración'}
                  </h2>
                  <p className="owner-desktop-subtitle">
                    {activeTab === 'supervision'
                      ? 'Relaciones supervisor–empleado por sede.'
                      : activeTab === 'dm'
                        ? 'Comunicados masivos por sede a empleados activos.'
                        : 'Administración centralizada de empresas y sedes'}
                  </p>
                </div>
                <div className="owner-desktop-header-actions">
                  <select
                    className="input-mobile py-1 px-2 text-sm min-h-0 h-10 min-w-[16rem]"
                    value={selectedCompanyId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedCompanyId(v);
                      localStorage.setItem('selected_company_id', v);
                    }}
                    disabled={companyLoading}
                  >
                    <option value="">Seleccionar empresa...</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-mobile btn-secondary text-lg font-semibold px-3 min-h-0 h-10"
                    onClick={handleQuickCreateCompany}
                    disabled={companyLoading}
                    title="Crear empresa rápida"
                  >
                    +
                  </button>
                  <button
                    onClick={() => setShowProfile(!showProfile)}
                    className="owner-desktop-profile-btn"
                    title="Abrir cuenta"
                  >
                    👤
                  </button>
                </div>
              </header>
              <main className="owner-desktop-content">
                {renderMainContent()}
              </main>
            </div>
          </div>
        </>
      ) : useEmployeeDesktopShell ? (
        <>
          <div className="employee-mobile-shell">
            <header className="mobile-header">
              <div className="flex items-center gap-2">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-8 w-8 flex-shrink-0 rounded-lg"
                  width={32}
                  height={32}
                />
                <h1>SiCheck</h1>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowProfile(!showProfile)}
                  className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center touch-feedback"
                >
                  👤
                </button>
              </div>
            </header>
            <main className="content-with-nav container-mobile">
              {renderMainContent()}
            </main>
            <nav className="bottom-nav">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`bottom-nav-item ${activeTab === item.id ? 'active' : ''}`}
                >
                  <span className="text-xl mb-1">{item.icon}</span>
                  <span className="text-xs">{item.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="employee-desktop-shell">
            <aside className="owner-desktop-sidebar">
              <div className="owner-desktop-brand">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                  alt=""
                  className="h-9 w-9 rounded-xl"
                  width={36}
                  height={36}
                />
                <div>
                  <p className="owner-desktop-brand-title">SiCheck</p>
                  <p className="owner-desktop-brand-sub">Supervisión y fichada</p>
                </div>
              </div>
              <div className="owner-desktop-nav">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveTab(item.id)}
                    className={`owner-desktop-nav-item ${activeTab === item.id ? 'active' : ''}`}
                  >
                    <span className="text-base">{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </aside>
            <div className="owner-desktop-main">
              <header className="owner-desktop-header">
                <div>
                  <h2 className="owner-desktop-title">{employeeDesktopHeader.title}</h2>
                  <p className="owner-desktop-subtitle">{employeeDesktopHeader.subtitle}</p>
                </div>
                <div className="owner-desktop-header-actions">
                  <button
                    type="button"
                    onClick={() => setShowProfile(!showProfile)}
                    className="owner-desktop-profile-btn"
                    title="Abrir cuenta"
                  >
                    👤
                  </button>
                </div>
              </header>
              <main className="owner-desktop-content">{renderMainContent()}</main>
            </div>
          </div>
        </>
      ) : (
        <>
          <header className="mobile-header">
            <div className="flex items-center gap-2">
              <img
                src={`${process.env.PUBLIC_URL || ''}/icon-192.svg`}
                alt=""
                className="h-8 w-8 flex-shrink-0 rounded-lg"
                width={32}
                height={32}
              />
              <h1>SiCheck</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center touch-feedback"
              >
                👤
              </button>
            </div>
          </header>
          <main className="content-with-nav container-mobile">
            {renderMainContent()}
          </main>
          <nav className="bottom-nav">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`bottom-nav-item ${activeTab === item.id ? 'active' : ''}`}
              >
                <span className="text-xl mb-1">{item.icon}</span>
                <span className="text-xs">{item.label}</span>
              </button>
            ))}
          </nav>
        </>
      )}

      {/* Profile Dropdown */}
      {showProfile && (
        <div className="modal-overlay" onClick={() => setShowProfile(false)}>
          <div className="modal-mobile center" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Cuenta</h3>
              <button onClick={() => setShowProfile(false)} className="text-2xl">×</button>
            </div>
            <div className="modal-content">
              <div className="space-y-4">
                {user?.role === 'owner' && (
                  <div className="space-y-2">
                    <label className="form-label-mobile">Empresa activa</label>
                    <div className="flex items-center gap-2">
                      <select
                        className="input-mobile py-1 px-2 text-sm min-h-0 h-10 flex-1"
                        value={selectedCompanyId}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedCompanyId(v);
                          localStorage.setItem('selected_company_id', v);
                          setShowProfile(false);
                        }}
                        disabled={companyLoading}
                      >
                        <option value="">Seleccionar empresa...</option>
                        {companies.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn-mobile btn-secondary text-lg font-semibold px-3 min-h-0 h-10"
                        onClick={handleQuickCreateCompany}
                        disabled={companyLoading}
                        title="Crear empresa rápida"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="form-label-mobile">Nombre</label>
                  <p className="text-lg font-medium">{user?.name}</p>
                </div>
                <div>
                  <label className="form-label-mobile">Email</label>
                  <p className="text-gray-600 break-all">{user?.email}</p>
                </div>
                <div>
                  <label className="form-label-mobile">Perfil</label>
                  <p className="text-gray-600 capitalize">{user?.role === 'owner' ? 'Dueño' : 'Empleado'}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="btn-mobile btn-danger w-full mt-6"
                >
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App app-shell">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardRouter />
                </PrivateRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Navigate to="/dashboard" />
                </PrivateRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;